import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Select, Space, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import GETJwtToken from "../../redux/authentication/authentication";
import {
  useLazyGetSimulationRunQuery,
  useLazyListSimulationRunsQuery,
  useLazyListSimulationScenariosQuery,
  useStartSimulationRunMutation,
  useStopSimulationRunMutation,
} from "../../redux/simulation/simulationAPI";
import { SimulationBankState } from "../../flatbuffers/generated/api/simulation-bank-state";
import type { SimulationBankStatusT } from "../../flatbuffers/generated/api/simulation-bank-status";
import type { SimulationRunT } from "../../flatbuffers/generated/api/simulation-run";
import { SimulationRunState } from "../../flatbuffers/generated/api/simulation-run-state";
import type { SimulationScenarioT } from "../../flatbuffers/generated/api/simulation-scenario";
import { formatUuidT } from "../../flatbuffers/display";
import { uuidsEqual } from "../../utils/uuid";

/** How often an active run's status is refreshed from the simulator, in milliseconds. */
export const RUN_POLL_MS = 3_000;

const text = (value: string | Uint8Array | null | undefined): string => String(value ?? "");

/** Whether `run` can still change: it has not stopped or failed yet. */
export function isActiveRun(run: SimulationRunT): boolean {
  return run.state !== SimulationRunState.STOPPED && run.state !== SimulationRunState.FAILED;
}

/** The tag colour of a run state. */
export function runStateColor(state: SimulationRunState): string {
  switch (state) {
    case SimulationRunState.RUNNING:
      return "success";
    case SimulationRunState.STARTING:
    case SimulationRunState.PENDING:
      return "processing";
    case SimulationRunState.STOPPING:
      return "warning";
    case SimulationRunState.FAILED:
      return "error";
    case SimulationRunState.STOPPED:
      return "default";
    default:
      return "default";
  }
}

/** The tag colour of a bank state. */
export function bankStateColor(state: SimulationBankState): string {
  switch (state) {
    case SimulationBankState.CONNECTED:
      return "success";
    case SimulationBankState.WAITING:
      return "gold";
    case SimulationBankState.FINISHED:
      return "blue";
    case SimulationBankState.FAILED:
      return "error";
    case SimulationBankState.DISABLED:
      return "default";
    default:
      return "default";
  }
}

/** How many of a run's banks VTS is connected to, out of those not disabled. */
export function connectedBanks(run: SimulationRunT): { connected: number; total: number } {
  const live = run.banks.filter((bank) => bank.state !== SimulationBankState.DISABLED);
  return {
    connected: live.filter((bank) => bank.state === SimulationBankState.CONNECTED).length,
    total: live.length,
  };
}

/** Newest first, by start time; runs not started yet come first. */
export function newestFirst(runs: readonly SimulationRunT[]): SimulationRunT[] {
  return [...runs].sort((a, b) => text(b.startedAt).localeCompare(text(a.startedAt)));
}

interface SimulationRunsTabProps {
  /** The organization whose runs are shown, as a dashed UUID string. */
  orgId: string;
}

/**
 * Start a saved scenario, watch each bank come up, stop it. While a run is
 * active its status is refreshed from the simulator every few seconds.
 */
function SimulationRunsTab({ orgId }: SimulationRunsTabProps) {
  const [t] = useTranslation("global");
  const [triggerScenarios, { data: scenarios = [] }] = useLazyListSimulationScenariosQuery();
  const [triggerRuns, { data: runs = [], isFetching }] = useLazyListSimulationRunsQuery();
  const [triggerRun] = useLazyGetSimulationRunQuery();
  const [startRun, { isLoading: starting }] = useStartSimulationRunMutation();
  const [stopRun] = useStopSimulationRunMutation();
  const [scenarioId, setScenarioId] = useState<string | undefined>(undefined);

  const refresh = useCallback(async () => {
    const token = await GETJwtToken();
    triggerScenarios({ orgId, token });
    triggerRuns({ orgId, token });
  }, [orgId, triggerScenarios, triggerRuns]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeIds = useMemo(
    () => runs.filter(isActiveRun).map((run) => formatUuidT(run.id)),
    [runs],
  );

  // The run list is what LiftCloud stored; only fetching a run asks the
  // simulator for its live state. So poll each active run, then re-list.
  useEffect(() => {
    if (activeIds.length === 0) return undefined;
    const timer = setInterval(async () => {
      const token = await GETJwtToken();
      await Promise.all(activeIds.map((id) => triggerRun({ id, token })));
      triggerRuns({ orgId, token });
    }, RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [activeIds, orgId, triggerRun, triggerRuns]);

  const scenarioFor = (run: SimulationRunT): SimulationScenarioT | undefined =>
    scenarios.find((scenario) => uuidsEqual(scenario.id, run.scenarioId));

  const onStart = async () => {
    if (!scenarioId) return;
    const token = await GETJwtToken();
    try {
      await startRun({ id: scenarioId, token }).unwrap();
      message.success(t("simulation.runStarted"));
      refresh();
    } catch (error) {
      const reason = (error as { data?: { message?: string } })?.data?.message;
      message.error(reason ?? t("simulation.runStartFailed"));
    }
  };

  const onStop = async (run: SimulationRunT) => {
    const token = await GETJwtToken();
    try {
      await stopRun({ id: formatUuidT(run.id), token }).unwrap();
      refresh();
    } catch (error) {
      const reason = (error as { data?: { message?: string } })?.data?.message;
      message.error(reason ?? t("simulation.stopFailed"));
    }
  };

  const bankColumns = (run: SimulationRunT): ColumnsType<SimulationBankStatusT> => [
    {
      title: t("simulation.bank"),
      key: "bank",
      render: (_, status) =>
        text(
          scenarioFor(run)?.banks.find((bank) => uuidsEqual(bank.bankId, status.bankId))?.bankName,
        ) || formatUuidT(status.bankId),
    },
    {
      title: t("simulation.state"),
      key: "state",
      render: (_, status) => (
        <Tag color={bankStateColor(status.state)}>
          {t(`simulation.bankState.${SimulationBankState[status.state]}`)}
        </Tag>
      ),
    },
    {
      title: t("simulation.vendorAddress"),
      key: "vendor",
      render: (_, status) => <code>{text(status.vendorAddress)}</code>,
    },
    {
      title: t("simulation.localAddress"),
      key: "local",
      render: (_, status) => <code>{text(status.localAddress)}</code>,
    },
    {
      title: t("simulation.packets"),
      key: "packets",
      render: (_, status) => `${status.packetsSent} / ${status.packetsReceived}`,
    },
    { title: t("simulation.loops"), dataIndex: "loopsCompleted", key: "loops" },
    { title: t("simulation.connections"), dataIndex: "connections", key: "connections" },
    {
      title: t("simulation.lastError"),
      key: "error",
      render: (_, status) =>
        text(status.lastError) && (
          <Typography.Text type="danger">{text(status.lastError)}</Typography.Text>
        ),
    },
  ];

  const columns: ColumnsType<SimulationRunT> = [
    {
      title: t("simulation.scenario"),
      key: "scenario",
      render: (_, run) => text(scenarioFor(run)?.name) || formatUuidT(run.scenarioId),
    },
    {
      title: t("simulation.state"),
      key: "state",
      render: (_, run) => (
        <Space size="small" wrap>
          <Tag color={runStateColor(run.state)}>
            {t(`simulation.runState.${SimulationRunState[run.state]}`)}
          </Tag>
          {text(run.lastError) && (
            <Typography.Text type="danger">{text(run.lastError)}</Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: t("simulation.tunnel"),
      key: "tunnel",
      render: (_, run) => (
        <Badge
          status={run.tunnelConnected ? "success" : "default"}
          text={run.tunnelConnected ? t("simulation.tunnelUp") : t("simulation.tunnelDown")}
        />
      ),
    },
    {
      title: t("simulation.banks"),
      key: "banks",
      render: (_, run) => t("simulation.banksConnected", connectedBanks(run)),
    },
    {
      title: t("simulation.started"),
      key: "started",
      render: (_, run) => `${text(run.startedBy)} · ${text(run.startedAt)}`,
    },
    {
      title: t("simulation.actions"),
      key: "actions",
      render: (_, run) =>
        isActiveRun(run) && (
          <Button size="small" danger onClick={() => onStop(run)}>
            {t("simulation.stop")}
          </Button>
        ),
    },
  ];

  return (
    <Space direction="vertical" className="generalStyles__width100">
      <Space wrap>
        <Select
          style={{ minWidth: 320 }}
          placeholder={t("simulation.chooseScenario")}
          value={scenarioId}
          options={scenarios.map((scenario) => ({
            value: formatUuidT(scenario.id),
            label: `${text(scenario.name)} · ${text(scenario.sandbox)}`,
          }))}
          onChange={(id: string) => setScenarioId(id)}
        />
        <Button type="primary" disabled={!scenarioId} loading={starting} onClick={onStart}>
          {t("simulation.start")}
        </Button>
      </Space>
      <Table
        size="small"
        rowKey={(run) => formatUuidT(run.id)}
        columns={columns}
        dataSource={newestFirst(runs)}
        loading={isFetching && runs.length === 0}
        pagination={false}
        expandable={{
          expandedRowRender: (run) => (
            <Table
              size="small"
              rowKey={(status) => formatUuidT(status.bankId)}
              columns={bankColumns(run)}
              dataSource={run.banks}
              pagination={false}
            />
          ),
          rowExpandable: (run) => run.banks.length > 0,
        }}
      />
    </Space>
  );
}

export default SimulationRunsTab;
